import React from 'react';
import PropTypes from 'prop-types';
import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';

import SessionCard from 'common/components/SessionCard';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { useModal } from 'common/components/TouchpointEditor/contexts/ModalManager';
import { mockSession } from 'common/components/TouchpointEditor/mockSessionsData';

import { descendantsDefaultProps, descendantsPropTypes } from './defaults';

import { SessionsListBlockContentContainer } from './SessionsList.styles';
import { SessionCardLabel } from './components/SessionCardLabel';
import SessionButtonsWrapper from './SessionButtonsWrapper';
import { NODE_ID_SPEAKER_MODALS, NODE_ID_SESSION_CARDS, NODE_ID_SESSIONS_CALENDAR_DOWNLOAD } from '../../constants';

export default function AttendeeSessionsList({
    selected,
    hovered,
    descendants: {
        card,
        title,
        date,
        location,
        speakerName,
        speakerTitleCompany,
        description,
        tag,
        statusLabel,
        registerSessionButton,
        registerWaitlistSessionButton,
        addToCalendarButton,
        unregisterSessionButton,
        unregisterWaitlistSessionButton,
    },
    meta,
}) {
    const node = useNode();

    const editor = useEditor((state) => ({ editing: state.editing, selectedNodeId: state.events.selected }));

    const modal = useModal();
    const {
        theme: { colors, activeFonts },
    } = useTouchpointData();
    const { filteredGuestSessionsData, data, isLoading, registeredSessions } = useSessions();

    const onClickIfEditing = () => editor.actions.selectNode(NODE_ID_SESSION_CARDS);
    const onClickIfNotEditing = (speakerInfo) =>
        speakerInfo.bio &&
        modal.open(NODE_ID_SPEAKER_MODALS, {
            speaker: speakerInfo,
        });

    const openSpeakerModal = useEditingCond(onClickIfEditing, onClickIfNotEditing);

    const mockActiveSession = mockSession;

    const shouldShowMockSession =
        editor.editing &&
        filteredGuestSessionsData.length === 0 &&
        editor.selectedNodeId === NODE_ID_SESSIONS_CALENDAR_DOWNLOAD;

    const isEmptyState = () => !data.length;
    const isLoadingState = () => isLoading;
    const shouldShowEmptyState =
        registeredSessions.length === 0 && !isEmptyState() && !isLoadingState() && !shouldShowMockSession;

    return (
        <SessionsListBlockContentContainer
            selected={selected}
            hovered={hovered}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            onClick={node.actions.onClick()}
        >
            {filteredGuestSessionsData.map((session) => (
                <SessionCard
                    key={session.id}
                    session={session}
                    colors={colors}
                    activeFonts={activeFonts}
                    cardStyles={card.styles}
                    titleStyles={title.styles}
                    dateStyles={date.styles}
                    speakerNameStyles={speakerName.styles}
                    speakerTitleCompanyStyles={speakerTitleCompany.styles}
                    descriptionStyles={description.styles}
                    tagStyles={tag.styles}
                    labelComponent={
                        <SessionCardLabel session={session} styles={statusLabel.styles} meta={statusLabel.meta} />
                    }
                    onClickSpeakerAvatar={openSpeakerModal}
                    footerComponent={
                        <SessionButtonsWrapper
                            session={session}
                            registerSessionButton={registerSessionButton}
                            registerWaitlistSessionButton={registerWaitlistSessionButton}
                            addToCalendarButton={addToCalendarButton}
                            unregisterSessionButton={unregisterSessionButton}
                            unregisterWaitlistSessionButton={unregisterWaitlistSessionButton}
                            disableContentEditing={!editor.editing || (!selected && !hovered)}
                        />
                    }
                />
            ))}
            {shouldShowEmptyState && meta.emptyMySessionsText}
            {shouldShowMockSession && (
                <SessionCard
                    session={mockActiveSession}
                    colors={colors}
                    activeFonts={activeFonts}
                    cardStyles={card.styles}
                    titleStyles={title.styles}
                    dateStyles={date.styles}
                    speakerNameStyles={speakerName.styles}
                    speakerTitleCompanyStyles={speakerTitleCompany.styles}
                    descriptionStyles={description.styles}
                    tagStyles={tag.styles}
                    labelComponent={
                        <SessionCardLabel
                            session={mockActiveSession}
                            styles={statusLabel.styles}
                            meta={statusLabel.meta}
                        />
                    }
                    onClickSpeakerAvatar={openSpeakerModal}
                    footerComponent={
                        <SessionButtonsWrapper
                            session={mockActiveSession}
                            registerSessionButton={registerSessionButton}
                            registerWaitlistSessionButton={registerWaitlistSessionButton}
                            addToCalendarButton={addToCalendarButton}
                            unregisterSessionButton={unregisterSessionButton}
                            unregisterWaitlistSessionButton={unregisterWaitlistSessionButton}
                        />
                    }
                />
            )}
        </SessionsListBlockContentContainer>
    );
}

AttendeeSessionsList.splash = {
    related: {
        settings: React.lazy(() => import('./SessionsList.tools')),
    },
    defaultProps: {
        styles: {},
        descendants: descendantsDefaultProps, // THIS IS AN EXCEPTION FOR THE AGENDA TOUCHPOINT
        meta: {
            emptyMySessionsText: 'You haven’t registered for any sessions yet. Start adding sessions to see them here.',
        },
    },
};

AttendeeSessionsList.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    descendants: PropTypes.shape(descendantsPropTypes).isRequired,
};

AttendeeSessionsList.defaultProps = {
    className: '',
};
